import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { 
  BarChart3,
  TrendingUp,
  Calendar,
  Download,
  RefreshCw,
  Mic,
  MessageSquare,
  HardDrive,
  Clock,
  DollarSign,
  AlertCircle,
  Loader2,
  Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const UsageAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [realTimeUsage, setRealTimeUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');

  useEffect(() => {
    fetchUsageData();
  }, [selectedPeriod]);

  const fetchUsageData = async () => {
    setLoading(true);
    setError('');

    try {
      // Fetch usage analytics
      const analyticsResponse = await fetch(`${BACKEND_URL}/api/usage/analytics?period=${selectedPeriod}`, {
        credentials: 'include'
      });
      
      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        setAnalytics(analyticsData);
      }

      // Fetch real-time usage
      const realTimeResponse = await fetch(`${BACKEND_URL}/api/usage/real-time`, {
        credentials: 'include'
      });

      if (realTimeResponse.ok) {
        const realTimeData = await realTimeResponse.json();
        setRealTimeUsage(realTimeData);
      }

    } catch (err) {
      console.error('Error fetching usage data:', err);
      setError('Failed to load usage analytics');
    } finally {
      setLoading(false);
    }
  };

  const getFeatureIcon = (featureKey) => {
    switch (featureKey) {
      case 'voice_minutes': return <Mic className="w-5 h-5 text-blue-600" />;
      case 'sms_count': return <MessageSquare className="w-5 h-5 text-green-600" />;
      case 'storage_gb': return <HardDrive className="w-5 h-5 text-purple-600" />;
      default: return <BarChart3 className="w-5 h-5 text-gray-600" />;
    }
  };

  const getFeatureName = (featureKey) => {
    const names = {
      'voice_minutes': 'Voice Minutes',
      'sms_count': 'SMS Messages',
      'storage_gb': 'Storage (GB)'
    };
    return names[featureKey] || featureKey;
  };

  const getUsageUnit = (featureKey) => {
    const units = {
      'voice_minutes': 'minutes',
      'sms_count': 'messages',
      'storage_gb': 'GB'
    };
    return units[featureKey] || 'units';
  };

  const getUsagePercentage = (used, limit) => {
    if (limit === -1 || limit === null) return 0; // Unlimited
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const exportUsageData = async () => {
    try {
      // In production, this would call an export endpoint
      const csvData = "Feature,Used,Cost,Period\n" + 
        (realTimeUsage?.features || []).map(f => 
          `${getFeatureName(f.feature_key)},${f.used_this_month},${f.cost_this_month_usd},${selectedPeriod}`
        ).join('\n');
      
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `usage-analytics-${selectedPeriod}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export usage data');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading usage analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Usage Analytics
          </h2>
          <p className="text-gray-600 dark:text-gray-300">Track your usage and billing in real-time</p>
        </div>
        <div className="flex gap-2">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
          >
            <option value="current_month">This Month</option>
            <option value="last_month">Last Month</option>
          </select>
          <Button onClick={exportUsageData} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={fetchUsageData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Real-Time Usage Overview */}
      {realTimeUsage && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {realTimeUsage.features.map((feature) => {
            const percentage = getUsagePercentage(feature.used_this_month, feature.allocation_limit);
            const isUnlimited = feature.allocation_limit === -1 || feature.allocation_limit === null;
            
            return (
              <div key={feature.feature_key} className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-gray-900 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {getFeatureIcon(feature.feature_key)}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {getFeatureName(feature.feature_key)}
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      ${feature.cost_this_month_usd.toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      This month
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Used:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {feature.used_this_month.toLocaleString()} {getUsageUnit(feature.feature_key)}
                    </span>
                  </div>
                  
                  {!isUnlimited && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Limit:</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {feature.allocation_limit.toLocaleString()} {getUsageUnit(feature.feature_key)}
                        </span>
                      </div>
                      
                      <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(percentage)}`}
                          style={{ width: `${Math.min(percentage, 100)}%` }}
                        ></div>
                      </div>
                      
                      <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                        {percentage.toFixed(1)}% used
                      </div>
                    </>
                  )}
                  
                  {isUnlimited && (
                    <div className="text-center py-2">
                      <span className="text-green-600 font-medium text-sm">Unlimited</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Usage Analytics Details */}
      {analytics && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-gray-900 dark:border-gray-700">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Usage Details - {selectedPeriod.replace('_', ' ').charAt(0).toUpperCase() + selectedPeriod.replace('_', ' ').slice(1)}
            </h3>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              {formatDate(analytics.start_date)} - {formatDate(analytics.end_date)}
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Cost</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                ${analytics.total_cost_usd.toFixed(2)}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Events</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {Object.values(analytics.usage_by_type).reduce((sum, usage) => sum + usage.event_count, 0)}
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Overage Cost</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                ${Object.values(analytics.usage_by_type).reduce((sum, usage) => sum + (usage.total_cost_usd || 0), 0).toFixed(2)}
              </div>
            </div>
          </div>

          {/* Detailed Usage Breakdown */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Usage Breakdown</h4>
            
            {Object.entries(analytics.usage_by_type).map(([eventType, usage]) => {
              const featureKey = eventType.includes('voice') ? 'voice_minutes' :
                                eventType.includes('sms') ? 'sms_count' : 'storage_gb';
              
              return (
                <div key={eventType} className="border border-gray-200 rounded-lg p-4 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getFeatureIcon(featureKey)}
                      <div>
                        <h5 className="font-medium text-gray-900 dark:text-gray-100">
                          {getFeatureName(featureKey)}
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {eventType.charAt(0).toUpperCase() + eventType.slice(1).replace('_', ' ')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-gray-900 dark:text-gray-100">
                        ${usage.total_cost_usd.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {usage.event_count} events
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600 dark:text-gray-300">Total Usage</div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {usage.total_amount.toLocaleString()} {getUsageUnit(featureKey)}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-gray-600 dark:text-gray-300">From Allocation</div>
                      <div className="font-medium text-green-600">
                        {usage.allocated_used.toLocaleString()} {getUsageUnit(featureKey)}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-gray-600 dark:text-gray-300">Overage</div>
                      <div className="font-medium text-red-600">
                        {usage.overage_used.toLocaleString()} {getUsageUnit(featureKey)}
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-gray-600 dark:text-gray-300">Cost</div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        ${usage.total_cost_usd.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {Object.keys(analytics.usage_by_type).length === 0 && (
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">No Usage Data</h4>
                <p className="text-gray-600 dark:text-gray-300">
                  No usage recorded for {selectedPeriod.replace('_', ' ')}.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Usage Testing Panel (Development) */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 dark:bg-gray-900 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-gray-100">Usage Testing</h3>
        <p className="text-sm text-gray-600 mb-4 dark:text-gray-300">
          Test the usage metering system with sample events
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Button 
            onClick={() => testUsageEvent('voice_end', 5.5, 'Test voice call')}
            variant="outline"
            size="sm"
          >
            <Mic className="w-4 h-4 mr-2" />
            Test Voice (5.5 min)
          </Button>
          
          <Button 
            onClick={() => testUsageEvent('sms_sent', 3, 'Test SMS batch')}
            variant="outline"
            size="sm"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Test SMS (3 msgs)
          </Button>
          
          <Button 
            onClick={() => testUsageEvent('storage_used', 1.2, 'Test storage')}
            variant="outline"
            size="sm"
          >
            <HardDrive className="w-4 h-4 mr-2" />
            Test Storage (1.2 GB)
          </Button>
        </div>
      </div>
    </div>
  );

  // Test function for development
  async function testUsageEvent(eventType, amount, description) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/usage/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          event_type: eventType,
          amount: amount,
          metadata: { description: description, test: true }
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        alert(`Usage tracked successfully!\nEvent: ${eventType}\nAmount: ${amount}\nCost: $${data.cost_usd?.toFixed(4) || '0.00'}`);
        // Refresh data
        await fetchUsageData();
      } else {
        alert(`Error: ${data.detail || 'Failed to track usage'}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  }
};

export default UsageAnalytics;